/**
 * GlobalAdminMfaCallout: the operational-risk tile on the User Analytics
 * Overview tab.
 *
 * Surfaces the single named risk operators care about most: Global Admins
 * without MFA. Renders only when the count is > 0; no gap, no callout.
 *
 * Carbon (DESIGN.md): a layer-01 tile with no kicker, all type upright. The
 * headline leads with the critical octagon (Coral is reserved for critical
 * severity, and the shape plus the bold message carry the meaning, never the
 * hue alone), one prescription sentence in secondary text, and a tertiary
 * link to the scoped MFA list.
 *
 * Voice contract (CLAUDE.md Product Voice):
 *   - Names the subject ("Global Admins"), not a row count.
 *   - One short prescription sentence. Period-then-clause, no em-dash.
 */

import { ButtonLink } from '@/components/carbon/Button'
import { IconArrowRight, IconSeverityCrit } from '@/components/carbon/icons'
import { Tile } from '@/components/carbon/Tile'

interface Props {
  /** Count of enabled member Global Admins without MFA. From
   *  `UserInsightsSummary.noMfaGlobalAdmins`. */
  count: number
}

/** 1-9 → word form, 10+ → numerals. Matches the convention in
 *  identity-brief.ts / posture-brief.ts so the voice stays consistent. */
function numberWord(n: number): string {
  const words = [
    'Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  ]
  return words[n] ?? String(n)
}

export default function GlobalAdminMfaCallout({ count }: Props) {
  // No gap, no callout.
  if (!count || count <= 0) return null

  // Subject-led headline. Word form for 1-9, numerals for 10+. Plural noun
  // only when count !== 1. Six words or fewer.
  //   "One Global Admin without MFA."
  //   "Two Global Admins without MFA."
  //   "12 Global Admins without MFA."
  const subject = count === 1 ? 'Global Admin' : 'Global Admins'

  // Prescription sentence. Period-then-clause, no em-dash. Same dignity as
  // the canonical Operations panel phrasing.
  const prescription = "They're the highest-impact MFA gap. Enrol them today."

  return (
    <Tile
      role="status"
      aria-live="polite"
      padding={24}
      className="mb-8 grid grid-cols-1 items-center gap-6 c-md:grid-cols-[minmax(0,1fr)_auto]"
    >
      <div className="flex min-w-0 items-start gap-3">
        <IconSeverityCrit size={20} className="mt-1 flex-none text-sev-crit" />
        <div className="min-w-0">
          <h3 className="m-0 text-heading font-normal text-text-primary">
            {numberWord(count)} {subject} without MFA.
          </h3>
          <p className="m-0 mt-2 text-body text-text-secondary">{prescription}</p>
        </div>
      </div>

      <ButtonLink
        to="/user-insights?tab=mfa&scope=globalAdmins"
        kind="tertiary"
        size="md"
        icon={<IconArrowRight />}
        className="max-c-md:w-full max-c-md:max-w-none"
        aria-label={
          count === 1
            ? 'View the Global Admin without MFA'
            : `View the ${count} Global Admins without MFA`
        }
      >
        View these admins
      </ButtonLink>
    </Tile>
  )
}
